import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import React, { useContext, useEffect, useState } from "react";
import axios from "../../../../../../axios";
import { AuthContext } from "../../../../../../context/states/authState";
import { AlertContext } from "../../../../../../context/states/alertState";

import PermissionNode from "./PermissionNode";
const PagePermissions = ({ userId }) => {
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [saving, setSaving] = useState(false);

  const { getUserPermissions, getAllPermissions } = useContext(AuthContext);
  const { alertDispatch } = useContext(AlertContext);

  const sidebarPages = permissions.filter((page) => page.showInSidebar);

  const hiddenPages = permissions.filter(
    (page) => !page.showInSidebar && page.type === "page",
  );

  const standaloneTabs = permissions.filter(
    (page) => !page.showInSidebar && page.type === "tab",
  );

  const buildParentMap = (nodes, map = {}) => {
    nodes.forEach((node) => {
      if (node.children?.length) {
        node.children.forEach((child) => {
          map[child._id] = node;
        });

        buildParentMap(node.children, map);
      }
    });

    return map;
  };

  useEffect(() => {
    loadPermissions();
  }, [userId]);

  const getAllChildIds = (node) => {
    let ids = [];

    if (node.children && node.children.length) {
      node.children.forEach((child) => {
        ids.push(child._id);

        ids = [...ids, ...getAllChildIds(child)];
      });
    }

    return ids;
  };

  const getAssignedPermissions = (nodes) => {
    let ids = [];

    nodes.forEach((node) => {
      if (node.assigned) {
        ids.push(node._id);
      }

      if (node.children?.length) {
        ids = [...ids, ...getAssignedPermissions(node.children)];
      }
    });

    return ids;
  };

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions(userId);

      console.log("userPermissions", userPermissions);

      setPermissions(userPermissions);
      setSelectedPermissions(getAssignedPermissions(userPermissions));
    } catch (err) {
      console.error(err);
    }
  };

  const handlePermissionChange = (node) => {
    const parentMap = buildParentMap(permissions);

    const childIds = getAllChildIds(node);
    const allIds = [node._id, ...childIds];

    const isSelected = selectedPermissions.includes(node._id);

    let updatedPermissions;

    if (isSelected) {
      if (node.type === "action") {
        // Remove only the action itself
        updatedPermissions = selectedPermissions.filter(
          (id) => id !== node._id,
        );
      } else {
        // Existing behaviour for pages/tabs
        updatedPermissions = selectedPermissions.filter(
          (id) => !allIds.includes(id),
        );

        let currentParent = parentMap[node._id];

        while (currentParent) {
          const hasSelectedChildren = currentParent.children.some((child) =>
            updatedPermissions.includes(child._id),
          );

          if (!hasSelectedChildren) {
            updatedPermissions = updatedPermissions.filter(
              (id) => id !== currentParent._id,
            );
          }

          currentParent = parentMap[currentParent._id];
        }
      }
    } else {
      updatedPermissions = [...new Set([...selectedPermissions, ...allIds])];

      let currentParent = parentMap[node._id];

      while (currentParent) {
        updatedPermissions.push(currentParent._id);

        currentParent = parentMap[currentParent._id];
      }
    }

    setSelectedPermissions([...new Set(updatedPermissions)]);
  };

  const savePermissions = async () => {
    try {
      setSaving(true);

      await axios.put(`/api/v1/users/${userId}/page-permissions`, {
        pagePermissions: selectedPermissions,
      });

      alertDispatch({
        type: "open",
        message: "Page permissions updated successfully",
      });
    } catch (err) {
      console.error(err);

      alertDispatch({
        type: "open",
        message: "Failed to update page permissions",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Access</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Show in Sidebar</TableCell>
            <TableCell>Type</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          <TableRow>
            <TableCell colSpan={4}>
              <strong>Sidebar Navigation</strong>
            </TableCell>
          </TableRow>

          {sidebarPages.map((node) => (
            <PermissionNode
              key={node._id}
              node={node}
              selectedPermissions={selectedPermissions}
              onToggle={handlePermissionChange}
            />
          ))}

          <TableRow>
            <TableCell colSpan={4}>
              <strong>Hidden Pages</strong>
            </TableCell>
          </TableRow>

          {hiddenPages.map((node) => (
            <PermissionNode
              key={node._id}
              node={node}
              selectedPermissions={selectedPermissions}
              onToggle={handlePermissionChange}
            />
          ))}
        </TableBody>
      </Table>

      <Button
        color="primary"
        variant="contained"
        onClick={savePermissions}
        disabled={saving}
        style={{ marginBlock: 32 }}
      >
        {saving ? "Saving..." : "Save Permissions"}
      </Button>
    </div>
  );
};

export default PagePermissions;
